import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiFetch from '../../../../utils/api';
import { useStats } from '../../../../context/StatsContext';
import './WatchAndEarn.css';

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmtCountdown(secs) {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return [h, m, s].map(n => String(n).padStart(2, '0')).join(':');
}

// YouTube player state constants (YT.PlayerState values)
const YT_PLAYING = 1;

// ─── component ────────────────────────────────────────────────────────────────

const WatchAndEarn = () => {
  const { refreshStats } = useStats();

  const [adStatus, setAdStatus] = useState(null);
  const [uiState, setUiState] = useState('loading');
  const [error, setError] = useState('');

  const playerContainerRef = useRef(null);
  const playerInstanceRef = useRef(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [muted, setMuted] = useState(true);

  const startSecondsRef = useRef(0);
  const watchDurationRef = useRef(60);

  // Watch session
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // Heartbeat tracking
  const heartbeatIntervalRef = useRef(null);
  const watchedSecondsRef = useRef(0);
  const isPlayingRef = useRef(false);
  const sessionCompletedRef = useRef(false);

  const [nextAdSecs, setNextAdSecs] = useState(0);
  const nextAdTimerRef = useRef(null);

  // ── Fetch status ──────────────────────────────────────────────────────────
  const fetchStatus = useCallback(async () => {
    setUiState('loading');
    setError('');
    try {
      const res = await apiFetch('/api/ad/status');
      const d = await res.json();
      setAdStatus(d);

      if (!d.eligible) {
        switch (d.reason) {
          case 'level_2_exempt':    setUiState('level2_exempt');     break;
          case 'no_pairs':          setUiState('no_pairs');          break;
          case 'no_eligible_video': setUiState('no_eligible_video'); break;
          case 'already_watched':
            setUiState('already_watched');
            setNextAdSecs(d.next_available_seconds || 0);
            break;
          case 'no_video_scheduled': setUiState('no_video');         break;
          default:                  setUiState('no_video');          break;
        }
      } else {
        startSecondsRef.current = d.start_seconds ?? 0;
        watchDurationRef.current = d.watch_duration_seconds || d.video?.duration_seconds || 60;
        setUiState('watching');
        setTimeRemaining(watchDurationRef.current);
      }
    } catch {
      setUiState('no_video');
      setError('Could not load ad status. Please refresh.');
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // ── "Come back tomorrow" countdown ────────────────────────────────────────
  useEffect(() => {
    if (uiState !== 'already_watched') return;
    if (nextAdTimerRef.current) clearInterval(nextAdTimerRef.current);
    nextAdTimerRef.current = setInterval(() => {
      setNextAdSecs(prev => {
        if (prev <= 1) { clearInterval(nextAdTimerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(nextAdTimerRef.current);
  }, [uiState]);

  // ── Load YouTube IFrame API ────────────────────────────────────────────────
  useEffect(() => {
    if (uiState !== 'watching') return;

    if (window.YT && window.YT.Player) {
      initPlayer();
      return;
    }

    if (!document.getElementById('yt-iframe-api')) {
      const tag = document.createElement('script');
      tag.id = 'yt-iframe-api';
      tag.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(tag);
    }

    window.onYouTubeIframeAPIReady = () => initPlayer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState]);

  const initPlayer = () => {
    if (!playerContainerRef.current || !adStatus?.video?.video_id) return;
    if (playerInstanceRef.current) return;

    playerInstanceRef.current = new window.YT.Player(playerContainerRef.current, {
      videoId: adStatus.video.video_id,
      playerVars: {
        autoplay: 1,
        controls: 0,
        disablekb: 1,
        rel: 0,
        modestbranding: 1,
        iv_load_policy: 3,
        fs: 0,
        mute: 1,
        playsinline: 1,
        start: startSecondsRef.current,
      },
      events: {
        onReady: handlePlayerReady,
        onError: handlePlayerError,
        onStateChange: handlePlayerStateChange,
      },
    });
  };

  const handlePlayerReady = async () => {
    setPlayerReady(true);
    try {
      const res = await apiFetch('/api/ad/start-watch', { method: 'POST', body: '{}' });
      const d = await res.json();
      if (!d.success) {
        setError(d.message || 'Could not start watch session.');
        return;
      }
      setSessionStarted(true);
      const duration = d.duration_seconds || watchDurationRef.current || 60;
      watchDurationRef.current = duration;
      watchedSecondsRef.current = d.watched_seconds || 0;
      setTimeRemaining(Math.max(0, duration - watchedSecondsRef.current));
      startHeartbeat();
    } catch {
      setError('Network error starting watch session.');
    }
  };

  const handlePlayerError = () => {
    setError('Video could not be loaded. Please try again later.');
  };

  const handlePlayerStateChange = (e) => {
    const playing = e.data === YT_PLAYING;
    isPlayingRef.current = playing;
    setIsPaused(!playing && sessionStarted && !sessionCompletedRef.current);
  };

  // Tick once per second. Only count the tick when the player is actually
  // playing AND the tab is visible. Anything else (paused, tab hidden,
  // buffering, ended) is ignored, so walking away does not earn anything.
  const startHeartbeat = () => {
    if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
    heartbeatIntervalRef.current = setInterval(async () => {
      if (sessionCompletedRef.current) return;
      if (!isPlayingRef.current) return;
      if (document.visibilityState !== 'visible') return;

      watchedSecondsRef.current = Math.min(
        watchDurationRef.current,
        watchedSecondsRef.current + 1
      );
      setTimeRemaining(Math.max(0, watchDurationRef.current - watchedSecondsRef.current));

      try {
        await apiFetch('/api/ad/heartbeat', {
          method: 'POST',
          body: JSON.stringify({ ticks: 1 }),
        });
      } catch {
        // Network blip — the client-side counter keeps the UI consistent;
        // the server will ultimately validate watched_seconds on complete.
      }

      if (watchedSecondsRef.current >= watchDurationRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
        await completeWatch();
      }
    }, 1000);
  };

  const completeWatch = async () => {
    try {
      const res = await apiFetch('/api/ad/complete-watch', { method: 'POST', body: '{}' });
      const d = await res.json();
      if (d.success) {
        sessionCompletedRef.current = true;
        setUiState('done');
        setAdStatus(prev => ({ ...(prev || {}), reward_message: d.message, remaining_ads: d.remaining_ads, pair_count: d.pair_count }));
        refreshStats();
      } else if (d.reason === 'session_expired' || d.reason === 'not_watched') {
        setError(d.message || 'Session expired. Please watch the ad again.');
        await resetAndRestart();
      } else {
        setError(d.message || 'Could not credit reward. Please contact support.');
      }
    } catch {
      setError('Network error completing watch. Please contact support.');
    }
  };

  const resetAndRestart = async () => {
    try {
      await apiFetch('/api/ad/reset-watch', { method: 'POST', body: '{}' });
    } catch { /* ignore */ }
    watchedSecondsRef.current = 0;
    setSessionStarted(false);
    setIsPaused(false);
    setTimeRemaining(watchDurationRef.current);
    if (playerInstanceRef.current) {
      try { playerInstanceRef.current.destroy(); } catch {}
      playerInstanceRef.current = null;
    }
    setPlayerReady(false);
    // Re-fetch status to re-init everything (fresh watch_started_at).
    fetchStatus();
  };

  // ── Toggle mute ───────────────────────────────────────────────────────────
  const toggleMute = () => {
    if (!playerInstanceRef.current) return;
    if (muted) {
      playerInstanceRef.current.unMute();
    } else {
      playerInstanceRef.current.mute();
    }
    setMuted(m => !m);
  };

  // ── Abandon session when user hides the tab or navigates away ─────────────
  useEffect(() => {
    if (!sessionStarted) return;

    const resetIfIncomplete = () => {
      if (sessionCompletedRef.current) return;
      if (watchedSecondsRef.current >= watchDurationRef.current) return;
      // Best-effort reset. On hard-unload the request may not complete, but
      // the server enforces watched_seconds on /complete-watch anyway — a
      // stale session cannot be redeemed.
      apiFetch('/api/ad/reset-watch', { method: 'POST', body: '{}', keepalive: true }).catch(() => {});
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        resetIfIncomplete();
      }
    };

    window.addEventListener('pagehide', resetIfIncomplete);
    window.addEventListener('beforeunload', resetIfIncomplete);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', resetIfIncomplete);
      window.removeEventListener('beforeunload', resetIfIncomplete);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [sessionStarted]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (heartbeatIntervalRef.current) clearInterval(heartbeatIntervalRef.current);
      if (nextAdTimerRef.current) clearInterval(nextAdTimerRef.current);
      if (playerInstanceRef.current) {
        try { playerInstanceRef.current.destroy(); } catch {}
        playerInstanceRef.current = null;
      }
      // If the user navigates away mid-watch, wipe the server-side session so
      // they cannot claim a reward by returning with a stale start timestamp.
      if (sessionStarted && !sessionCompletedRef.current &&
          watchedSecondsRef.current < watchDurationRef.current) {
        apiFetch('/api/ad/reset-watch', { method: 'POST', body: '{}' }).catch(() => {});
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Render states ──────────────────────────────────────────────────────────

  if (uiState === 'loading') {
    return (
      <div className="wae-center">
        <div className="wae-spinner" />
        <p className="wae-hint">Checking your ad eligibility…</p>
      </div>
    );
  }

  if (uiState === 'level2_exempt') {
    return (
      <div className="wae-center">
        <div className="wae-icon">🏆</div>
        <h3 className="wae-title">Ads Completed</h3>
        <p className="wae-msg">Ad Play is Only Available for users with 10/10 members</p>
        <p className="wae-hint">You have completed all available ad slots. Congratulations!</p>
      </div>
    );
  }

  if (uiState === 'no_pairs') {
    return (
      <div className="wae-center">
        <div className="wae-icon">🔒</div>
        <h3 className="wae-title">Next Ad Locked</h3>
        <p className="wae-msg">
          {adStatus?.message || "Next Ad دیکھنے کے لیے Binary Pair complete کریں"}
        </p>
        <div className="wae-pair-info">
          <div className="wae-pair-stat">
            <span className="wae-pair-stat__label">Ads Watched</span>
            <span className="wae-pair-stat__value">{adStatus?.ads_watched || 0}</span>
          </div>
          <div className="wae-pair-stat">
            <span className="wae-pair-stat__label">Pairs Completed</span>
            <span className="wae-pair-stat__value">{adStatus?.pair_count || 0}</span>
          </div>
        </div>
        <p className="wae-hint">
          Complete a Binary Pair by adding members to both your Left and Right teams, then your next ad will unlock automatically.
        </p>
      </div>
    );
  }

  if (uiState === 'already_watched') {
    return (
      <div className="wae-center">
        <div className="wae-icon wae-icon--green">✅</div>
        <h3 className="wae-title">Today's Reward Earned!</h3>
        <p className="wae-msg">Rs. 25 has been credited to your account.</p>
        <div className="wae-pair-info">
          <div className="wae-pair-stat">
            <span className="wae-pair-stat__label">Ads Watched</span>
            <span className="wae-pair-stat__value">{adStatus?.ads_watched || 0}</span>
          </div>
          <div className="wae-pair-stat">
            <span className="wae-pair-stat__label">Remaining Ads</span>
            <span className="wae-pair-stat__value">{adStatus?.remaining_ads || 0}</span>
          </div>
        </div>
        <div className="wae-countdown-box">
          <span className="wae-countdown-label">Next ad available in</span>
          <span className="wae-countdown">{fmtCountdown(nextAdSecs)}</span>
        </div>
      </div>
    );
  }

  if (uiState === 'done') {
    return (
      <div className="wae-center">
        <div className="wae-icon wae-icon--green">🎉</div>
        <h3 className="wae-title">Reward Credited!</h3>
        <p className="wae-msg">
          {adStatus?.reward_message || 'Rs. 25 has been added to your available balance.'}
        </p>
        <div className="wae-pair-info">
          <div className="wae-pair-stat">
            <span className="wae-pair-stat__label">Remaining Ads</span>
            <span className="wae-pair-stat__value">{adStatus?.remaining_ads ?? '—'}</span>
          </div>
          <div className="wae-pair-stat">
            <span className="wae-pair-stat__label">Pairs</span>
            <span className="wae-pair-stat__value">{adStatus?.pair_count ?? '—'}</span>
          </div>
        </div>
        <p className="wae-hint">Come back tomorrow for your next ad.</p>
      </div>
    );
  }

  if (uiState === 'no_eligible_video') {
    return (
      <div className="wae-center">
        <div className="wae-icon">📺</div>
        <h3 className="wae-title">No Ad Available</h3>
        <p className="wae-msg">No video is available for your session right now. Please check back later.</p>
      </div>
    );
  }

  if (uiState === 'no_video') {
    return (
      <div className="wae-center">
        <div className="wae-icon">📺</div>
        <h3 className="wae-title">No Ad This Week</h3>
        <p className="wae-msg">No video has been scheduled for this week yet. Check back soon.</p>
        {error && <p className="wae-error">{error}</p>}
      </div>
    );
  }

  // ── 'watching' state ─────────────────────────────────────────────────────
  const video = adStatus?.video;
  const watchDuration = watchDurationRef.current || video?.duration_seconds || 60;
  const watched = watchDuration - timeRemaining;
  const progressPct = watchDuration > 0
    ? Math.round((watched / watchDuration) * 100)
    : 0;

  return (
    <div className="wae-page">
      <h2 className="wae-page-title">Watch &amp; Earn</h2>
      <p className="wae-page-sub">
        Watch the full ad segment to earn <strong>Rs. 25</strong>. The timer
        only counts while the ad is playing and this tab is focused — do not
        leave the page.
      </p>

      {adStatus?.remaining_ads != null && (
        <div className="wae-ad-progress">
          <span>Ad {(adStatus?.ads_watched || 0) + 1} of {adStatus?.ads_allowed || '—'}</span>
          <span className="wae-ad-progress__pairs">Pairs: {adStatus?.pair_count || 0}</span>
        </div>
      )}

      {error && <div className="wae-error-banner">{error}</div>}

      <div className="wae-player-outer">
        <div className="wae-overlay" />
        <div ref={playerContainerRef} className="wae-player-frame" />
        {playerReady && (
          <button className="wae-mute-btn" onClick={toggleMute} title={muted ? 'Unmute' : 'Mute'}>
            {muted ? '🔇 Unmute' : '🔊 Mute'}
          </button>
        )}
      </div>

      {sessionStarted && (
        <div className="wae-progress-wrap">
          <div className="wae-progress-bar" style={{ width: `${progressPct}%` }} />
        </div>
      )}

      <div className="wae-timer">
        {sessionStarted
          ? timeRemaining > 0
            ? (isPaused
                ? `Paused — timer resumes when the ad plays (${fmtCountdown(timeRemaining)} left)`
                : `Time remaining: ${fmtCountdown(timeRemaining)}`)
            : 'Processing reward…'
          : 'Loading video…'}
      </div>

      {video && (
        <p className="wae-video-title">{video.title}</p>
      )}
    </div>
  );
};

export default WatchAndEarn;
